import React, { useEffect, useRef, useState } from "react";
import { LOGO_URL } from "@/components/Logo";

const ROADRUNNER_SVG = LOGO_URL;
const DASH_COUNT = 3;
const DASH_DURATION_MS = 2700;
const INTERVAL_MS = 5000;

let audioCtx = null;

function playVroom() {
  try {
    const Ctx = window.AudioContext || window.webkitAudioContext;
    if (!Ctx) return;
    if (!audioCtx) audioCtx = new Ctx();
    if (audioCtx.state === "suspended") audioCtx.resume();
    const now = audioCtx.currentTime;

    const osc = audioCtx.createOscillator();
    const gain = audioCtx.createGain();
    osc.type = "sawtooth";
    osc.frequency.setValueAtTime(60, now);
    osc.frequency.exponentialRampToValueAtTime(180, now + 0.4);
    osc.frequency.exponentialRampToValueAtTime(320, now + 1.0);
    osc.frequency.exponentialRampToValueAtTime(140, now + 1.8);
    gain.gain.setValueAtTime(0, now);
    gain.gain.linearRampToValueAtTime(0.4, now + 0.05);
    gain.gain.linearRampToValueAtTime(0.5, now + 0.8);
    gain.gain.linearRampToValueAtTime(0.0, now + 2.4);
    const filter = audioCtx.createBiquadFilter();
    filter.type = "lowpass";
    filter.frequency.setValueAtTime(800, now);
    filter.frequency.linearRampToValueAtTime(2000, now + 1.0);
    filter.Q.value = 6;
    osc.connect(filter); filter.connect(gain); gain.connect(audioCtx.destination);
    osc.start(now); osc.stop(now + 2.5);

    const zoom = audioCtx.createOscillator();
    const zg = audioCtx.createGain();
    zoom.type = "square";
    zoom.frequency.setValueAtTime(400, now);
    zoom.frequency.exponentialRampToValueAtTime(1400, now + 0.6);
    zoom.frequency.exponentialRampToValueAtTime(2400, now + 1.4);
    zg.gain.setValueAtTime(0, now);
    zg.gain.linearRampToValueAtTime(0.1, now + 0.2);
    zg.gain.linearRampToValueAtTime(0.0, now + 2.0);
    zoom.connect(zg); zg.connect(audioCtx.destination);
    zoom.start(now); zoom.stop(now + 2.0);

    const dop = audioCtx.createOscillator();
    const dg = audioCtx.createGain();
    dop.type = "triangle";
    dop.frequency.setValueAtTime(1600, now + 0.8);
    dop.frequency.exponentialRampToValueAtTime(400, now + 2.2);
    dg.gain.setValueAtTime(0, now + 0.8);
    dg.gain.linearRampToValueAtTime(0.15, now + 1.0);
    dg.gain.linearRampToValueAtTime(0.0, now + 2.3);
    dop.connect(dg); dg.connect(audioCtx.destination);
    dop.start(now + 0.8); dop.stop(now + 2.3);
  } catch (e) {
    console.warn("vroom audio failed", e);
  }
}

export default function RoadrunnerAlert({ trigger, onDone }) {
  const [dashIndex, setDashIndex] = useState(0);
  const [visible, setVisible] = useState(false);
  const [origin, setOrigin] = useState({ x: 40, y: 40 });
  const timers = useRef([]);

  const computeOrigin = () => {
    // Find the brand logo in the nav so the bird can hop out of it
    try {
      const logoEl = document.querySelector('[data-testid="brand-logo"] img');
      if (logoEl) {
        const r = logoEl.getBoundingClientRect();
        return { x: r.left + r.width * 0.25, y: r.top + r.height * 0.25 };
      }
    } catch {}
    return { x: 40, y: 40 };
  };

  const wiggleLogo = () => {
    try {
      const logoEl = document.querySelector('[data-testid="brand-logo"] img');
      if (logoEl) {
        logoEl.classList.remove("logo-wiggle-during-alert");
        // trigger reflow so animation restarts
        // eslint-disable-next-line no-unused-expressions
        void logoEl.offsetWidth;
        logoEl.classList.add("logo-wiggle-during-alert");
      }
    } catch {}
  };

  useEffect(() => {
    if (!trigger) return;
    timers.current.forEach(clearTimeout);
    timers.current = [];
    setVisible(true);
    let currentDash = 0;

    const runDash = () => {
      currentDash += 1;
      setOrigin(computeOrigin());
      wiggleLogo();
      setDashIndex(currentDash);
      playVroom();
      timers.current.push(setTimeout(() => setDashIndex(0), DASH_DURATION_MS + 50));
      if (currentDash < DASH_COUNT) {
        timers.current.push(setTimeout(runDash, INTERVAL_MS));
      } else {
        timers.current.push(setTimeout(() => {
          setVisible(false);
          setDashIndex(0);
          if (onDone) onDone();
        }, DASH_DURATION_MS + 400));
      }
    };

    runDash();

    return () => {
      timers.current.forEach(clearTimeout);
      timers.current = [];
    };
  }, [trigger, onDone]);

  if (!visible) return null;

  const spriteStyle = { "--hop-x": `${origin.x}px`, "--hop-y": `${origin.y}px` };

  return (
    <>
      <div className="alert-flash-persistent" data-testid="alert-flash"/>
      <div className="dash-counter" data-testid="dash-counter">
        <span className="dash-counter-icon">🚨</span>
        NEW REQUEST · Dash {Math.max(1, dashIndex)}/{DASH_COUNT}
      </div>
      {dashIndex > 0 && (
        <div key={`dash-${dashIndex}`} className="roadrunner-sprite-big" data-testid="roadrunner-sprite" style={spriteStyle}>
          <img src={ROADRUNNER_SVG} alt="Roadrunner"/>
          <div className="rr-boom" data-testid="roadrunner-boom">
            <span>V</span><span>R</span><span>O</span><span>O</span><span>O</span><span>M</span><span>!</span>
          </div>
        </div>
      )}
    </>
  );
}

export { playVroom };
